// Шпаргалка клавиш «?» (задача D5, ui-spec §6): окно «Клавиши» — из той же таблицы `HOTKEYS` (D4), что и обработчики.
// Две группы: «Везде» и «На этом экране» — только клавиши, доступные роли вошедшего. Внизу — «Esc — закрыть окно или
// панель». У одного действия может быть несколько клавиш (J и ↓) — одна строка «J / ↓».
import type { Role } from '@contract/index.js';
import type { ScreenId } from '@/app/routes';
import { useScreen } from '@/app/section';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { HOTKEYS, type HotkeyScope } from '@/lib/hotkeys';

/** Клавиши какого экрана показывать на каждом экране пульта. */
const SCOPE_OF_SCREEN: Readonly<Partial<Record<ScreenId, HotkeyScope>>> = {
  readiness: 'readiness',
  overview: 'overview',
  dialogs: 'dialogs',
  dialog: 'dialog',
  review: 'review',
  testRuns: 'tests',
  testRun: 'tests',
};

export interface ShortcutRow {
  readonly id: string;
  readonly keysRu: string;
  readonly labelRu: string;
}

/** Строки шпаргалки: клавиши области `scope`, доступные роли; клавиши одного действия — в одной строке. */
export function shortcutRows(scope: HotkeyScope, role: Role): ShortcutRow[] {
  const rows = new Map<string, { keys: string[]; label: string }>();
  for (const hotkey of HOTKEYS) {
    if (hotkey.scope !== scope || !hotkey.roles.includes(role)) continue;
    const row = rows.get(hotkey.id);
    if (row === undefined) rows.set(hotkey.id, { keys: [hotkey.keysRu], label: hotkey.labelRu });
    else row.keys.push(hotkey.keysRu);
  }
  return [...rows].map(([id, row]) => ({ id, keysRu: row.keys.join(' / '), labelRu: row.label }));
}

function Keys({ keysRu }: { readonly keysRu: string }) {
  return (
    <span className="flex shrink-0 flex-wrap justify-end gap-1">
      {keysRu.split(' / ').map((key) => (
        <kbd
          key={key}
          className="inline-flex h-6 min-w-6 items-center justify-center rounded-sm border border-border-strong bg-card px-1.5 font-sans text-sm text-foreground"
        >
          {key}
        </kbd>
      ))}
    </span>
  );
}

function Group({ titleRu, rows }: { readonly titleRu: string; readonly rows: readonly ShortcutRow[] }) {
  return (
    <section className="flex flex-col gap-1" aria-label={titleRu}>
      <h3 className="text-sm text-muted-foreground">{titleRu}</h3>
      <ul className="flex flex-col">
        {rows.map((row) => (
          <li key={row.id} className="flex min-h-8 items-center justify-between gap-4 border-b border-border py-1 last:border-b-0">
            <span className="min-w-0">{row.labelRu}</span>
            <Keys keysRu={row.keysRu} />
          </li>
        ))}
      </ul>
    </section>
  );
}

export interface ShortcutsDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly role: Role;
}

export function ShortcutsDialog({ open, onOpenChange, role }: ShortcutsDialogProps) {
  const screen = useScreen();
  const scope = screen === undefined ? undefined : SCOPE_OF_SCREEN[screen];
  const here = scope === undefined ? [] : shortcutRows(scope, role);
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
      }}
    >
      <DialogContent data-hotkeys="off" className="max-w-105">
        <DialogHeader>
          <DialogTitle>Клавиши</DialogTitle>
        </DialogHeader>
        <Group titleRu="Везде" rows={shortcutRows('global', role)} />
        {here.length > 0 && <Group titleRu="На этом экране" rows={here} />}
        <p className="flex items-center justify-between gap-4 text-sm text-muted-foreground">
          <span>Esc — закрыть окно или панель</span>
        </p>
      </DialogContent>
    </Dialog>
  );
}
